import React from 'react';

/**
 * Staff-facing error boundary. Wraps the authenticated CRM so a single
 * component crash falls back to a friendly screen with recovery actions
 * instead of leaving the admin/staff user staring at a blank page.
 */
export class CrmErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('CRM error boundary caught:', error, info);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoDashboard = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 bg-slate-50 dark:bg-slate-900"
        data-testid="crm-error-boundary"
      >
        <div className="max-w-lg w-full bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 p-8">
          <div className="flex items-start gap-4 mb-5">
            <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-950/40 flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
              </svg>
            </div>
            <div className="flex-1">
              <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-1">This page hit an error</h1>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                A component on this page crashed. The rest of the CRM is still working — try reloading or jumping back to the dashboard. If it keeps happening, let the dev team know with the details below.
              </p>
            </div>
          </div>

          <div className="flex gap-3 flex-wrap">
            <button
              onClick={this.handleReload}
              className="px-4 py-2 rounded-lg bg-amber-500 text-white font-medium text-sm hover:bg-amber-600 transition-colors"
              data-testid="crm-error-reload"
            >
              Reload page
            </button>
            <button
              onClick={this.handleGoDashboard}
              className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 font-medium text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              data-testid="crm-error-dashboard"
            >
              Back to Dashboard
            </button>
          </div>

          {this.state.error && (
            <details className="mt-6 text-left text-xs">
              <summary className="cursor-pointer text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">Technical details (for support)</summary>
              <pre className="mt-2 p-3 bg-slate-100 dark:bg-slate-900 rounded text-slate-600 dark:text-slate-300 whitespace-pre-wrap break-all">
                {String(this.state.error?.message || this.state.error)}
                {this.state.error?.stack ? `\n\n${this.state.error.stack.split('\n').slice(0, 6).join('\n')}` : ''}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

export default CrmErrorBoundary;
